import { useEffect, useState } from 'react';
import {
  IonPage, IonHeader, IonToolbar, IonTitle, IonContent, IonButtons, IonMenuButton,
  IonButton, IonIcon, IonSpinner, IonCard, IonCardHeader, IonCardTitle,
  IonCardContent, IonModal, IonItem, IonInput, IonTextarea, IonSelect, IonSelectOption, IonChip, IonRefresher, IonRefresherContent, useIonRouter,
} from '@ionic/react';
import { addOutline, pencilOutline, rocketOutline, pauseOutline, playOutline, trashOutline } from 'ionicons/icons';
import { useTranslation } from 'react-i18next';
import { PLATFORM_IDS, PLATFORMS, type PlatformId } from '@ai-media/shared';
import {
  useCampaignStore,
  type CampaignSummary,
  type CreateCampaignInput,
  type UpdateCampaignInput,
} from '@/store/campaigns.store';
import StatusPill from '@/components/StatusPill';
import PlatformChip from '@/components/PlatformChip';
import EmptyState from '@/components/EmptyState';

export default function CampaignsPage() {
  const { t } = useTranslation();
  const router = useIonRouter();
  const campaigns = useCampaignStore((s) => s.campaigns);
  const loading = useCampaignStore((s) => s.loading);
  const refresh = useCampaignStore((s) => s.refresh);
  const create = useCampaignStore((s) => s.create);
  const update = useCampaignStore((s) => s.update);
  const publish = useCampaignStore((s) => s.publish);
  const pause = useCampaignStore((s) => s.pause);
  const deleteCampaign = useCampaignStore((s) => s.deleteCampaign);
  const [open, setOpen] = useState(false);
  const [editingCampaign, setEditingCampaign] = useState<CampaignSummary | null>(null);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar>
          <IonButtons slot="start"><IonMenuButton /></IonButtons>
          <IonTitle>{t('campaigns.title')}</IonTitle>
          <IonButtons slot="end">
            <IonButton onClick={() => setOpen(true)}><IonIcon slot="icon-only" icon={addOutline} /></IonButton>
          </IonButtons>
        </IonToolbar>
      </IonHeader>
      <IonContent>
        <IonRefresher slot="fixed" onIonRefresh={(event) => { void refresh().then(() => event.detail.complete()); }}>
          <IonRefresherContent />
        </IonRefresher>

        {loading && campaigns.length === 0 ? (
          <div className="empty-state"><IonSpinner /></div>
        ) : campaigns.length === 0 ? (
          <EmptyState icon={rocketOutline} title={t('campaigns.noCampaigns')}
            action={<IonButton onClick={() => setOpen(true)}>{t('campaigns.createFirstCampaign')}</IonButton>} />
        ) : (
          campaigns.map((campaign) => (
            <IonCard key={campaign.id} className="post-card" onClick={() => router.push(`/app/campaigns/${campaign.id}`)}>
              <IonCardHeader style={{ paddingBottom: 4 }}>
                <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                  <StatusPill status={campaign.status} />
                  <span style={{ marginLeft: 'auto', fontSize: '0.8rem', color: 'var(--ion-color-medium)' }}>
                    {campaign.post_count} {t('campaigns.posts')}
                  </span>
                </div>
                <IonCardTitle style={{ fontSize: '1.05rem', marginTop: 6 }}>{campaign.name}</IonCardTitle>
              </IonCardHeader>
              <IonCardContent>
                {campaign.description && <p className="post-excerpt">{campaign.description}</p>}
                <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', margin: '8px 0' }}>
                  {campaign.platform_ids.map((id) => <PlatformChip key={id} platform={id} />)}
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  {campaign.status === 'planning' && (
                    <IonButton size="small" onClick={(event) => { event.stopPropagation(); void publish(campaign.id); }}>
                      <IonIcon slot="start" icon={playOutline} />
                      {t('campaigns.publishCampaign')}
                    </IonButton>
                  )}
                  {campaign.status === 'active' && (
                    <IonButton size="small" fill="outline" onClick={(event) => { event.stopPropagation(); void pause(campaign.id); }}>
                      <IonIcon slot="start" icon={pauseOutline} />
                      {t('campaigns.stopCampaign')}
                    </IonButton>
                  )}
                  <IonButton
                    size="small"
                    fill="outline"
                    color="medium"
                    onClick={(event) => {
                      event.stopPropagation();
                      setEditingCampaign(campaign);
                      setOpen(true);
                    }}
                    aria-label={t('campaigns.editCampaign')}
                  >
                    <IonIcon slot="start" icon={pencilOutline} />
                  </IonButton>
                  <IonButton
                    size="small"
                    fill="outline"
                    color="danger"
                    onClick={(event) => {
                      event.stopPropagation();
                      void deleteCampaign(campaign.id);
                    }}
                    aria-label={t('campaigns.deleteCampaign')}
                  >
                    <IonIcon slot="start" icon={trashOutline} />
                    {t('campaigns.deleteCampaign')}
                  </IonButton>
                </div>
              </IonCardContent>
            </IonCard>
          ))
        )}

        <CampaignModal
  open={open}
  onClose={() => {
    setOpen(false);
    setEditingCampaign(null);
  }}
  campaign={editingCampaign}
  onCreate={create}
  onUpdate={editingCampaign
    ? ((_id: string, input: UpdateCampaignInput) => update(_id, input))
    : undefined}
/>
      </IonContent>
    </IonPage>
  );
}

function CampaignModal(props: {
  open: boolean;
  onClose: () => void;
  campaign?: CampaignSummary | null;
  onCreate: (input: CreateCampaignInput) => Promise<unknown>;
  onUpdate?: (id: string, input: UpdateCampaignInput) => Promise<unknown>;
}) {
  const { t } = useTranslation();
  const isEditing = props.campaign != null;
  const [name, setName] = useState(isEditing ? props.campaign!.name : '');
  const [description, setDescription] = useState(isEditing ? (props.campaign!.description ?? '') : '');
  const [goal, setGoal] = useState(isEditing ? (props.campaign!.goal ?? '') : '');
  const [selected, setSelected] = useState<Set<PlatformId>>(
    isEditing ? new Set(props.campaign!.platform_ids) : new Set()
  );
  const [durationDays, setDurationDays] = useState(isEditing ? (props.campaign!.duration_days ?? 14) : 14);
  const [postsPerWeek, setPostsPerWeek] = useState(isEditing ? (props.campaign!.posts_per_week ?? 3) : 3);
  const [keywords, setKeywords] = useState(
    isEditing ? ((props.campaign!.ai_brief as { keywords?: string[] } | null)?.keywords ?? []).join(', ') : ''
  );
  const [busy, setBusy] = useState(false);

  // Re-prefill when a different campaign is edited, and reset to create
  // defaults when the modal closes.
  useEffect(() => {
    if (isEditing && props.campaign) {
      setName(props.campaign.name);
      setDescription(props.campaign.description ?? '');
      setGoal(props.campaign.goal ?? '');
      setSelected(new Set(props.campaign.platform_ids));
      setDurationDays(props.campaign.duration_days ?? 14);
      setPostsPerWeek(props.campaign.posts_per_week ?? 3);
      setKeywords(
        ((props.campaign.ai_brief as { keywords?: string[] } | null)?.keywords ?? []).join(', ')
      );
    } else {
      setName('');
      setDescription('');
      setGoal('');
      setSelected(new Set());
      setDurationDays(14);
      setPostsPerWeek(3);
      setKeywords('');
    }
  }, [isEditing, props.campaign]);

  const submit = async () => {
    if (!name.trim() || selected.size === 0) return;
    setBusy(true);
    try {
      const input = {
        name: name.trim(),
        description: description.trim() || undefined,
        goal: goal.trim() || undefined,
        platformIds: [...selected],
        durationDays,
        postsPerWeek,
        autoSchedule: true,
        aiBrief: { keywords: keywords.split(/[\s,]+/).filter(Boolean) },
      };
      if (isEditing && props.onUpdate && props.campaign) {
        await props.onUpdate(props.campaign.id, input as UpdateCampaignInput);
      } else {
        await props.onCreate(input as CreateCampaignInput);
      }
      props.onClose();
    } finally {
      setBusy(false);
    }
  };

  return (
    <IonModal isOpen={props.open} onDidDismiss={props.onClose}>
      <IonHeader>
        <IonToolbar>
          <IonButtons slot="start"><IonButton onClick={props.onClose}>{t('common.cancel')}</IonButton></IonButtons>
          <IonTitle>{isEditing ? t('campaigns.editCampaign') : t('campaigns.newCampaign')}</IonTitle>
        </IonToolbar>
      </IonHeader>
      <IonContent>
        <div style={{ padding: '10px 14px 40px' }}>
          <IonItem><IonInput label={`${t('campaigns.name')} *`} labelPlacement="stacked" value={name}
            onIonInput={(event) => setName((event.target as HTMLIonInputElement).value as string)} /></IonItem>
          <IonItem><IonTextarea label={t('campaigns.description')} labelPlacement="stacked" rows={3} value={description}
            onIonInput={(event) => setDescription((event.target as HTMLIonTextareaElement).value as string)} /></IonItem>
          <IonItem><IonSelect label={t('campaigns.objective')} labelPlacement="stacked" value={goal}
            onIonChange={(event) => setGoal(event.detail.value as string)}>
            <IonSelectOption value="awareness">{t('campaigns.goalAwareness')}</IonSelectOption>
            <IonSelectOption value="engagement">{t('campaigns.goalEngagement')}</IonSelectOption>
            <IonSelectOption value="leadgen">{t('campaigns.goalLeadgen')}</IonSelectOption>
            <IonSelectOption value="traffic">{t('campaigns.goalTraffic')}</IonSelectOption>
            <IonSelectOption value="sales">{t('campaigns.goalSales')}</IonSelectOption>
          </IonSelect></IonItem>
          <IonItem><IonInput label={t('campaigns.keywords')} labelPlacement="stacked" value={keywords}
            onIonInput={(event) => setKeywords((event.target as HTMLIonInputElement).value as string)} /></IonItem>
          <IonItem><IonInput label={t('campaigns.duration')} labelPlacement="stacked" type="number" value={String(durationDays)}
            onIonInput={(event) => setDurationDays(Number((event.target as HTMLIonInputElement).value) || 14)} /></IonItem>
          <IonItem><IonInput label={t('campaigns.schedulePosts')} labelPlacement="stacked" type="number" value={String(postsPerWeek)}
            onIonInput={(event) => setPostsPerWeek(Number((event.target as HTMLIonInputElement).value) || 3)} /></IonItem>

          <h6 className="section-title">{t('campaigns.platforms')} *</h6>
          <div className="composer-platforms">
            {PLATFORM_IDS.map((id) => (
              <IonChip key={id} color={selected.has(id) ? 'primary' : 'medium'} outline={!selected.has(id)}
                onClick={() => setSelected((current) => {
                  const next = new Set(current);
                  if (next.has(id)) next.delete(id); else next.add(id);
                  return next;
                })}>
                {PLATFORMS[id].label}
              </IonChip>
            ))}
          </div>
          <p style={{ fontSize: '0.8rem', color: 'var(--ion-color-medium)', margin: '0 6px' }}>{t('campaigns.autoSchedule')}</p>
          <IonButton expand="block" className="ion-margin-top" disabled={busy} onClick={() => void submit()}>
            {busy ? <IonSpinner name="dots" /> : (isEditing ? t('common.save') : t('campaigns.newCampaign'))}
          </IonButton>
        </div>
      </IonContent>
    </IonModal>
  );
}

